import ProductionStorage from './productionStorage';

export interface UserTrialData {
  startDate: string;
  endDate: string;
  daysLeft: number;
  isExpired: boolean;
  planId: string;
  isActive: boolean;
  autoConvertToPlan?: string; // Plan to convert to after trial
}

const USER_TRIAL_STORAGE_KEY = 'bookapt_user_trial_data';
const USER_SUBSCRIPTION_STORAGE_KEY = 'bookapt_user_subscription_data';

export class UserTrialManager {
  // Start a 30-day trial for a user
  static async startTrial(planId: string = 'premium'): Promise<UserTrialData> {
    const startDate = new Date();
    const endDate = new Date();
    endDate.setDate(startDate.getDate() + 30); // 30-day trial

    const trialData: UserTrialData = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      daysLeft: 30,
      isExpired: false,
      planId: planId,
      isActive: true,
      autoConvertToPlan: planId, // Default to same plan
    };

    await ProductionStorage.setItem(USER_TRIAL_STORAGE_KEY, JSON.stringify(trialData));
    await ProductionStorage.removeItem(USER_SUBSCRIPTION_STORAGE_KEY); // Ensure no active subscription
    
    console.log('🎉 User trial started:', trialData);
    return trialData;
  }

  // Get current trial data
  static async getTrialData(): Promise<UserTrialData | null> {
    try {
      const data = await ProductionStorage.getItem(USER_TRIAL_STORAGE_KEY);
      if (data) {
        const trialData: UserTrialData = JSON.parse(data);
        const now = new Date();
        const endDate = new Date(trialData.endDate);
        const diffTime = endDate.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        trialData.daysLeft = Math.max(0, diffDays);
        trialData.isExpired = diffDays <= 0;
        trialData.isActive = !trialData.isExpired;

        // Update storage with current status
        await ProductionStorage.setItem(USER_TRIAL_STORAGE_KEY, JSON.stringify(trialData));
        return trialData;
      }
      return null;
    } catch (error) {
      console.error('Error getting trial data:', error);
      return null;
    }
  }

  // Check if user has an active trial
  static async hasActiveTrial(): Promise<boolean> {
    const trialData = await this.getTrialData();
    return trialData ? trialData.isActive && !trialData.isExpired : false;
  }

  // Check if user has any subscription (trial or paid)
  static async hasActiveSubscription(): Promise<boolean> {
    const trialData = await this.getTrialData();
    const subscriptionData = await ProductionStorage.getItem(USER_SUBSCRIPTION_STORAGE_KEY);
    
    return (trialData && trialData.isActive && !trialData.isExpired) || !!subscriptionData;
  }

  // Convert trial to paid subscription
  static async convertTrialToPaid(planId: string): Promise<boolean> {
    try {
      const trialData = await this.getTrialData();
      if (!trialData) {
        throw new Error('No trial data found');
      }

      const subscriptionData = {
        planId: planId,
        startDate: new Date().toISOString(),
        isActive: true,
        convertedFromTrial: true,
        originalTrialStartDate: trialData.startDate,
      };

      await ProductionStorage.setItem(USER_SUBSCRIPTION_STORAGE_KEY, JSON.stringify(subscriptionData));
      await ProductionStorage.removeItem(USER_TRIAL_STORAGE_KEY); // Remove trial data
      
      console.log('✅ Trial converted to paid subscription:', subscriptionData);
      return true;
    } catch (error) {
      console.error('Error converting trial to paid:', error);
      return false;
    }
  }

  // Cancel trial (user cancels before it expires)
  static async cancelTrial(): Promise<boolean> {
    try {
      await ProductionStorage.removeItem(USER_TRIAL_STORAGE_KEY);
      console.log('❌ User trial cancelled');
      return true;
    } catch (error) {
      console.error('Error cancelling trial:', error);
      return false;
    }
  }

  // Extend trial (admin function)
  static async extendTrial(days: number): Promise<boolean> {
    try {
      const trialData = await this.getTrialData();
      if (!trialData) {
        throw new Error('No trial data found');
      }

      const currentEndDate = new Date(trialData.endDate);
      currentEndDate.setDate(currentEndDate.getDate() + days);
      
      trialData.endDate = currentEndDate.toISOString();
      trialData.daysLeft += days;

      await ProductionStorage.setItem(USER_TRIAL_STORAGE_KEY, JSON.stringify(trialData));
      console.log(`📅 Trial extended by ${days} days`);
      return true;
    } catch (error) {
      console.error('Error extending trial:', error);
      return false;
    }
  }

  // Get trial status message
  static async getTrialStatusMessage(): Promise<string> {
    const trialData = await this.getTrialData();
    if (!trialData) {
      return 'No trial active';
    }

    if (trialData.isExpired) {
      return 'Trial has expired';
    }

    if (trialData.daysLeft === 1) {
      return 'Trial expires tomorrow';
    }

    if (trialData.daysLeft <= 7) {
      return `Trial expires in ${trialData.daysLeft} days`;
    }

    return `Trial active - ${trialData.daysLeft} days remaining`;
  }

  // Clear all trial and subscription data
  static async clearAllData(): Promise<void> {
    await ProductionStorage.removeItem(USER_TRIAL_STORAGE_KEY);
    await ProductionStorage.removeItem(USER_SUBSCRIPTION_STORAGE_KEY);
    console.log('🧹 All user trial and subscription data cleared');
  }

  // Simulate trial expiration for testing
  static async simulateTrialExpiration(): Promise<void> {
    const trialData = await this.getTrialData();
    if (trialData) {
      const expiredDate = new Date();
      expiredDate.setDate(expiredDate.getDate() - 1); // Yesterday
      
      trialData.endDate = expiredDate.toISOString();
      trialData.daysLeft = 0;
      trialData.isExpired = true;
      trialData.isActive = false;

      await ProductionStorage.setItem(USER_TRIAL_STORAGE_KEY, JSON.stringify(trialData));
      console.log('⏰ Trial simulated as expired');
    }
  }

  // Create trial with specific days left for testing
  static async createTrialWithDaysLeft(days: number): Promise<UserTrialData> {
    await this.clearAllData(); // Clear existing data
    
    const startDate = new Date();
    const endDate = new Date();
    endDate.setDate(startDate.getDate() + days);

    const trialData: UserTrialData = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      daysLeft: days,
      isExpired: days <= 0,
      planId: 'premium',
      isActive: days > 0,
      autoConvertToPlan: 'premium',
    };

    await ProductionStorage.setItem(USER_TRIAL_STORAGE_KEY, JSON.stringify(trialData));
    console.log(`🎯 Created trial with ${days} days left`);
    return trialData;
  }
}

export default UserTrialManager;



